import React, { useState, useEffect } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronLeft, Instagram, Facebook, Heart } from "lucide-react";
import { useLang } from "../../i18n";
import { useLenis } from "../../lib/motion.jsx";
import { LangSwitch } from "../../components/LangSwitch";

const LOGO_JEUGD = "/inkad-jeugd-logo.jpg";

const navItems = [
  { to: "/jeugd/activiteiten", key: "nav_activiteiten" },
  { to: "/jeugd/huiswerkklas", key: "nav_huiswerkklas" },
  { to: "/jeugd/vakantiekampen", key: "nav_vakantiekampen" },
  { to: "/jeugd/liefdadigheid", key: "nav_liefdadigheid" },
  { to: "/jeugd/galerij", key: "nav_galerij" },
];

export default function JeugdLayout() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  useLenis();
  useEffect(() => { window.scrollTo(0, 0); setOpen(false); }, [pathname]);

  return (
    <div className="jeugd-theme relative min-h-screen" data-testid="jeugd-layout">
      <header className="sticky top-0 z-50 border-b border-black/5 bg-white/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 md:px-8 h-16">
          <Link to="/jeugd" className="flex items-center gap-3" data-testid="jeugd-logo-home">
            <img src={LOGO_JEUGD} alt="Inkad Jeugd" className="h-10 w-auto" />
            <span className="font-fredoka font-bold text-lg hidden sm:block">Inkad Jeugd</span>
          </Link>
          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((it) => (
              <NavLink key={it.to} to={it.to} data-testid={`jeugd-nav-${it.key}`}
                className={({ isActive }) => `px-4 py-2 rounded-full text-sm font-semibold transition-colors duration-200 ${isActive ? "bg-[#E07A5F]/10 text-[#E07A5F]" : "text-[#52525B] hover:text-[#E07A5F]"}`}>
                {t(it.key)}
              </NavLink>
            ))}
            <Link to="/jeugd/inschrijven" data-testid="jeugd-nav-register"
              className="ml-2 rounded-full bg-[#E07A5F] px-6 py-2.5 text-sm font-bold text-white transition-transform duration-200 hover:scale-105">
              {t("nav_inschrijven")}
            </Link>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/jeugd/inschrijven" data-testid="jeugd-nav-register-mobile"
              className="lg:hidden rounded-full bg-[#E07A5F] px-4 py-1.5 text-xs font-bold text-white">
              {t("nav_inschrijven")}
            </Link>
            <div className="hidden md:block"><LangSwitch /></div>
            <Link to="/" data-testid="jeugd-back-hub" className="hidden md:flex items-center gap-1 text-xs uppercase tracking-widest text-black/40 hover:text-black transition-colors">
              <ChevronLeft size={14} /> {t("backToInkad")}
            </Link>
            <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="jeugd-menu-open"><Menu /></button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-[#F7F5F0] lg:hidden" data-testid="jeugd-mobile-menu">
            <div className="flex items-center justify-between px-5 h-16 border-b border-black/5">
              <img src={LOGO_JEUGD} alt="Inkad Jeugd" className="h-10" />
              <button onClick={() => setOpen(false)} data-testid="jeugd-menu-close"><X /></button>
            </div>
            <div className="flex flex-col p-6 gap-2">
              {navItems.map((it, i) => (
                <motion.div key={it.to} initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: i * 0.05, type: "spring", stiffness: 200 }}>
                  <NavLink to={it.to} className="block font-fredoka text-3xl font-semibold text-[#262626] py-2">{t(it.key)}</NavLink>
                </motion.div>
              ))}
              <Link to="/jeugd/inschrijven" className="mt-4 rounded-full bg-[#E07A5F] px-6 py-3 text-center font-bold text-white">{t("nav_inschrijven")}</Link>
              <div className="mt-6 flex items-center justify-between">
                <LangSwitch />
                <Link to="/" className="text-xs uppercase tracking-widest text-black/40 flex items-center gap-1"><ChevronLeft size={14} />{t("backToInkad")}</Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <main><Outlet /></main>

      <footer className="bg-white border-t border-black/5 mt-20">
        <div className="mx-auto max-w-7xl px-6 py-14 grid gap-10 md:grid-cols-4">
          <div className="md:col-span-2">
            <img src={LOGO_JEUGD} alt="Inkad Jeugd" className="h-14 mb-4" />
            <p className="text-sm text-[#52525B] max-w-sm leading-relaxed">{t("jeugdDesc")}</p>
            <div className="mt-5 flex gap-3">
              <a href="https://www.instagram.com/inkad_diegem/" target="_blank" rel="noreferrer" data-testid="jeugd-footer-instagram" className="rounded-full bg-[#F7F5F0] p-2.5 text-[#52525B] hover:text-[#E07A5F] transition-colors" aria-label="Instagram"><Instagram size={18} /></a>
              <a href="https://www.facebook.com/inkaddiegem/" target="_blank" rel="noreferrer" data-testid="jeugd-footer-facebook" className="rounded-full bg-[#F7F5F0] p-2.5 text-[#52525B] hover:text-[#E07A5F] transition-colors" aria-label="Facebook"><Facebook size={18} /></a>
              <a href="https://www.tiktok.com/@inkad.diegem" target="_blank" rel="noreferrer" data-testid="jeugd-footer-tiktok" className="rounded-full bg-[#F7F5F0] p-2.5 text-[#52525B] hover:text-[#E07A5F] transition-colors" aria-label="TikTok">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" /></svg>
              </a>
            </div>
          </div>
          <div>
            <h4 className="font-fredoka font-bold text-[#262626] mb-4">Inkad Jeugd</h4>
            <ul className="space-y-2 text-sm text-[#52525B]">
              {navItems.map((it) => <li key={it.to}><Link to={it.to} className="hover:text-[#E07A5F] transition-colors">{t(it.key)}</Link></li>)}
              <li><Link to="/jeugd/inschrijven" className="hover:text-[#E07A5F] transition-colors">{t("nav_inschrijven")}</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-fredoka font-bold text-[#262626] mb-4">{t("contact")}</h4>
            <ul className="space-y-2 text-sm text-[#52525B]">
              <li className="font-semibold text-[#262626]">Inkad VZW</li>
              <li>Kerktorenstraat 31, 1831 Diegem</li>
              <li>Ondernemingsnr. BE 0743.549.936</li>
              <li><a href="mailto:info@inkad.be" className="hover:text-[#E07A5F] transition-colors">info@inkad.be</a></li>
              <li><a href="tel:+32484331788" className="hover:text-[#E07A5F] transition-colors">0484 33 17 88</a></li>
              <li><Link to="/fc-inkad" className="text-[#E07A5F] font-semibold hover:underline">{t("otherBranch")} →</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-black/5 py-5 text-center text-xs text-[#52525B] flex items-center justify-center gap-1">
          © {new Date().getFullYear()} Inkad VZW · Ondernemingsnr. BE 0743.549.936 · {t("footerRights")} <Heart size={11} className="text-[#E07A5F] fill-[#E07A5F]" />
        </div>
      </footer>
    </div>
  );
}
